import React, { useEffect, useState } from 'react';
// @ts-ignore
import styles from './MultiSearchWithCheckList.module.scss';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import { components, MultiValue } from 'react-select';
import Checkbox from '../../../../../Components/Base/Checkbox/Checkbox';
import ReactAsyncSelect from 'react-select/async';
import { MultiSearchWithCheckListItemType } from '../../../Enums/MultiSearchWithCheckListItemType.enum';
import useTranslation from '../../../../../Hooks/useTranslation';

interface Props {
	onChange?: any;
	placeholder?: string;
	required?: boolean;
	value?: any;
	label?: string;
	firstUpper?: boolean;
	className?: string;
	portal?: boolean;
	hasError?: boolean;
	onSearch: (
		inputValue: string,
		callback: (options: MultiSearchWithCheckListValue[]) => void,
	) => void;
}

type MultiSearchWithCheckListItemData = MultiSearchWithCheckListItemPatientData;

interface MultiSearchWithCheckListItemPatientData {
	birthNumber?: string;
	age?: string;
	address?: string;
	phoneNumber?: string;
	userRelations?: {
		workplaceName: string;
		relatedUserName: string;
	}[];
	display?: boolean;
	isToggled?: boolean;
	setIsToggled?: (isToggled: boolean) => void;
}

export interface MultiSearchWithCheckListValue extends SelectValue {
	type: MultiSearchWithCheckListItemType;
	itemData?: MultiSearchWithCheckListItemData;
}

const InputOption = ({
	getStyles,
	Icon,
	isDisabled,
	isSelected,
	children,
	innerProps,
	...rest
}: any) => {
	const [isActive, setIsActive] = useState(false);
	const { t } = useTranslation();
	const onMouseDown = () => setIsActive(true);
	const onMouseUp = () => setIsActive(false);
	const onMouseLeave = () => setIsActive(false);

	// styles
	let bg = 'transparent';
	if (isActive) bg = '#B2D4FF';

	const style = {
		alignItems: 'center',
		backgroundColor: bg,
		color: 'inherit',
		display: 'flex ',
	};

	// prop assignment
	const props = {
		...innerProps,
		onMouseDown,
		onMouseUp,
		onMouseLeave,
		style,
	};

	const itemType = rest?.data?.type;
	const itemData: MultiSearchWithCheckListItemData = rest?.data?.itemData;

	switch (itemType) {
		case MultiSearchWithCheckListItemType.ITEM_GROUP_TITLE:
			return <div className={styles.groupTitle}>{children}</div>;
		case MultiSearchWithCheckListItemType.DIVIDER:
			return <hr />;
		case MultiSearchWithCheckListItemType.TOGGLE_MORE:
			return (
				<div
					className={styles.toggleMore}
					onClick={prev =>
						itemData?.setIsToggled && itemData.setIsToggled(!prev)
					}>
					{itemData?.isToggled
						? 'component.messageRecipientsMultiSelectWithCheckList.showLess'
						: 'component.messageRecipientsMultiSelectWithCheckList.showMore'}
				</div>
			);
		case MultiSearchWithCheckListItemType.DOCTOR:
		case MultiSearchWithCheckListItemType.NON_DOCTOR:
		case MultiSearchWithCheckListItemType.GROUP:
			return (
				<components.Option
					{...rest}
					isDisabled={isDisabled}
					isSelected={isSelected}
					getStyles={getStyles}
					innerProps={props}>
					<div className={styles.patientWrapper}>
						<Checkbox value={isSelected} className={styles.checkbox} />
						<div className={styles.content}>
							<div className={styles.title}>{children}</div>
						</div>
					</div>
				</components.Option>
			);
		case MultiSearchWithCheckListItemType.PATIENT:
			let subtitleLine1: string[] = [];
			let subtitleLine2: string[] = [];

			if (itemData?.birthNumber) {
				subtitleLine1.push(
					`${t('base.shared.birthNumberShort')}: ${itemData.birthNumber}`,
				);
			}

			if (itemData?.age) {
				subtitleLine1.push(`${itemData.age} ${t('base.years.many')}`);
			}

			if (itemData?.address) {
				subtitleLine1.push(itemData.address);
			}

			if (itemData?.phoneNumber) {
				subtitleLine1.push(itemData.phoneNumber);
			}

			if (itemData?.userRelations) {
				itemData.userRelations.forEach((relation, key) => {
					subtitleLine2.push(
						`${relation.workplaceName} - ${relation.relatedUserName}`,
					);
				});
			}

			return (
				<components.Option
					{...rest}
					isDisabled={isDisabled}
					isSelected={isSelected}
					getStyles={getStyles}
					innerProps={props}>
					<div className={styles.patientWrapper}>
						<Checkbox value={isSelected} className={styles.checkbox} />
						<div className={styles.content}>
							<div className={styles.title}>{children}</div>
							<div className={styles.subtitle}>{subtitleLine1.join(', ')}</div>
							<div className={styles.subtitle}>{subtitleLine2.join(', ')}</div>
						</div>
					</div>
				</components.Option>
			);
	}

	return (
		<components.Option
			{...rest}
			isDisabled={isDisabled}
			isSelected={isSelected}
			getStyles={getStyles}
			innerProps={props}>
			<div className={styles.wrapper}>
				<Checkbox value={isSelected} className={styles.checkbox} />
				<div className={styles.title}>{children}</div>
			</div>
		</components.Option>
	);
};

const MultiSearchWithCheckList: React.FC<Props> = ({
	onChange,
	placeholder,
	value,
	portal,
	onSearch,
	hasError,
}) => {
	const [innerValue, setInnerValue] = useState<any>();
	const { t } = useTranslation();

	const components: any = {
		Option: InputOption,
	};

	const handleChange = (
		newValue: MultiValue<MultiSearchWithCheckListValue>,
	) => {
		if (!newValue) {
			onChange?.({
				value: '',
				label: '',
			});
		} else {
			onChange?.(newValue);
		}
	};

	useEffect(() => {
		setInnerValue(
			value?.label === '' || value?.value === ''
				? null
				: value === undefined
				? null
				: value,
		);
	}, [value]);

	// @ts-ignore
	return (
		<ReactAsyncSelect
			className={hasError ? 'invalid' : ''}
			classNamePrefix={'reactSearchSelect'}
			cacheOptions={false}
			loadOptions={onSearch}
			value={innerValue}
			onChange={handleChange}
			placeholder={t(placeholder)}
			components={components}
			isClearable={true}
			// @ts-ignore
			menuPortalTarget={portal ? document.body : false}
			isMulti={true}
			closeMenuOnSelect={false}
			hideSelectedOptions={false}
			isSearchable={true}
		/>
	);
};

export default MultiSearchWithCheckList;
